// /////SearcResultModal.jsx
// /////SearcResultModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Button, Table, Checkbox, message, Spin } from 'antd'; // Remove Modal from antd
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Tooltip } from '@mui/material'; // Add MUI Dialog
import '../index.css';
import '../css/TooltipStyles.css';
import SimpleExternalLinkNewWindow from '../functions/LinkFlex';
import api from '../functions/api';
// DAS-2327: shared tooltip content for Reward÷Risk / Return Prob / Exp Q Return
import {
  ExpQReturnTooltip,
  ReturnProbabilityTooltip,
  RewardRiskTooltip,
} from '../helpers/returnMetricsTooltips';
import { useSelector, useDispatch } from 'react-redux';
import SMAGraph from './SMAGraph';
import PositionDetails from './PositionDetails';
import ESG_DetailsContent from './ESG_DetailsContent';
import Stockinfo_TablesContent from './Stockinfo_TablesContent';
import TickerFundamentals from './Ticker_Fundamentals';
import LLMFormattedPrompt from './LLMFormattedPrompt';
import MarketChangesAndTrends from './MarketChangesAndTrends';
import AlphaCardBlock from './AlphaCardBlock'; // DAS-3258
import { Popover } from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const transpose = (matrix) => matrix[0].map((_, colIndex) => matrix.map((row) => row[colIndex]));

const ThreeTablesModal = ({ tickerValue, open, onClose, userId }) => {
  const demoMode = useSelector(state => state.demoMode.isDemoMode);
  const maybeBlur = (text) => demoMode ? <span className="blur-text">{text}</span> : text;
  const [transactionData, setTransactionData] = useState([]);
  const allOpportunityAlerts = useSelector((state) => state?.portfolio?.data?.opportunities_alerts || []);
  const [trendAnchorEl, setTrendAnchorEl] = useState(null);
  const trendPopoverOpen = Boolean(trendAnchorEl);
  
  // DAS-1887: PDF download ref
  const contentRef = useRef(null);

  const opportunityAlertsForTicker = tickerValue
    ? allOpportunityAlerts.filter(alert =>
        alert.ticker?.trim().toLowerCase() === tickerValue.trim().toLowerCase()
      )
    : [];

  const pfDataAlerts = useSelector((state) => state?.portfolio?.data?.pf_data_alert);
  const pfDataAlertForTicker = pfDataAlerts && Array.isArray(pfDataAlerts)
    ? pfDataAlerts.find(alert =>
        alert[2] && tickerValue &&
        alert[2].toLowerCase() === tickerValue.toLowerCase()
      )
    : null;

  const portfolioData = useSelector((state) => state?.portfolio?.data?.portfolio) || [];
  const isTickerInPortfolio = portfolioData && Array.isArray(portfolioData)
    ? portfolioData.some((item) => {
        const itemTicker = item?.Ticker;
        return itemTicker && tickerValue && itemTicker.toLowerCase() === tickerValue.toLowerCase();
      })
    : false;

  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteChanged, setFavoriteChanged] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [stockValue, setStockValue] = useState(null);
  const [tablesData, setTablesData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [alertsData, setAlertsData] = useState([]);
  const [esgDetails, setEsgDetails] = useState(null);
  const [sectorValue, setSectorValue] = useState(null);

  const stored_pfalertData = useSelector((state) => state?.portfolio?.data?.pf_alert);
  const stored_favalertData = useSelector((state) => state?.favorites?.data?.favorites_alert_data);
  const normalizedTickerValue = tickerValue ? tickerValue.trim().toLowerCase() : '';
  const tickerAlerts = stored_pfalertData ? stored_pfalertData.filter(alert => alert.ticker.trim().toLowerCase() === normalizedTickerValue) : [];
  const tickerAlert = stored_pfalertData ? stored_pfalertData.find(alert => alert.ticker === tickerValue) : null;
  const favAlert = stored_favalertData ? stored_favalertData.find(alert => alert.ticker === tickerValue) : null;

  const [smaGraphData, setSMAGraphData] = useState(null);
  const [isTableDataLoading, setIsTableDataLoading] = useState(true);
  const [hasValidData, setHasValidData] = useState(true);
  const DP_dataset_json = useSelector(state => state?.portfolio?.parametersData?.DP_dataset_json);

  const tickerPortfolioData = portfolioData.find((item) =>
    item?.Ticker && tickerValue && item.Ticker.toLowerCase() === tickerValue.toLowerCase()
  );
  const [llmModalOpen, setLlmModalOpen] = useState(false);

  const processTransactionData = (transactionData) => {
    return transactionData.map(transaction => ({
      date: new Date(transaction.date).toISOString().split('T')[0],
      price: transaction.price,
      type: transaction.buysell,
      quantity: transaction.quantity
    }));
  };

  const fetchRef = useRef({ tickerValue: '', isTickerInPortfolio: false });
  const transactions = useSelector(state => state.portfolio?.data?.transactions || []);

  /* ---------- central spinner state ---------- */
  const [loadingCount, setLoadingCount] = useState(0);
  const startLoading = () => {
    setLoadingCount(c => {
      const newCount = c + 1;
      console.log('🔍 Loading started, count:', newCount);
      return newCount;
    });
  };
  const stopLoading = () => {
    setLoadingCount(c => {
      const newCount = Math.max(0, c - 1);
      console.log('✅ Loading stopped, count:', newCount);
      return newCount;
    });
  };
  const isLoading = loadingCount > 0;

  useEffect(() => {
    if (!open) {
      // reset spinner when modal closes
      setLoadingCount(0);
    } else {
      // reset spinner when modal opens to ensure clean state
      setLoadingCount(0);
      
      // Add a timeout to automatically stop loading if it gets stuck
      const timeoutId = setTimeout(() => {
        setLoadingCount(0);
      }, 10000); // 10 seconds timeout
      
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  useEffect(() => {
    if (isTickerInPortfolio &&
        (fetchRef.current.tickerValue !== tickerValue || fetchRef.current.isTickerInPortfolio !== isTickerInPortfolio)) {
      const filteredTransactions = transactions.filter(transaction =>
        transaction.ticker && transaction.ticker.trim().toUpperCase() === tickerValue.trim().toUpperCase()
      );
      const formattedTransactionData = processTransactionData(filteredTransactions);
      setTransactionData(formattedTransactionData);
      fetchRef.current.tickerValue = tickerValue;
      fetchRef.current.isTickerInPortfolio = isTickerInPortfolio;
    }
  }, [transactions, tickerValue, isTickerInPortfolio]);

  // DAS-2250: Fetch stock data first (tables render immediately), then graph (own spinner)
  useEffect(() => {
    if (!tickerValue) return;
    let cancelled = false;
    // DAS-2250: Clear ALL previous ticker data so stale content doesn't show
    setStockData(null);
    setStockValue(null);
    setTablesData(null);
    setMetadata(null);
    setEsgDetails(null);
    setSectorValue(null);
    setSMAGraphData(null);
    setHasValidData(true);
    startLoading();
    const fetchAll = async () => {
      try {
        // Step 1: Fetch stock/table data
        const stockResponse = await api.get("/stock_data", { params: { symbol: tickerValue } });

        if (stockResponse.status === 404) {
          message.error('This ticker is not yet in our database. You may submit a request to have it added.');
          if (!cancelled) setHasValidData(false);
          return;
        }

        if (stockResponse.status === 200 && stockResponse.data) {
          let consensus = stockResponse.data.consensus;
          try {
            consensus.consensus_explanation_object = JSON.parse(consensus.consensus_explanation);
          } catch (e) {
            console.error("❌ Failed to parse consensus_explanation:", e);
            consensus.consensus_explanation_object = null;
          }

          if (!cancelled) {
            setStockData({
              ...stockResponse.data.ticker_fundamentals,
              consensus,
              fundamentals_trend_analysis_data: stockResponse.data.fundamentals_trend_analysis_data,
              fundamental_position_data: stockResponse.data.fundamental_position_data,  // DAS-1928
            });
            setStockValue(stockResponse.data.stock);
            setSectorValue(stockResponse.data.sector);
            setTablesData(stockResponse.data.tables);
            setMetadata(stockResponse.data.metadata);
            setHasValidData(true);
            setEsgDetails(stockResponse.data.esgDetails);
            stopLoading(); // Tables visible now — spinner gone
          }

          // Step 2: Fetch graph AFTER tables are shown (graph has its own spinner)
          try {
            const graphResponse = await api.post("/get_SMA_graph", { ticker: tickerValue });
            if (graphResponse.data && graphResponse.data.graph_data) {
              const { graph_data, currency } = graphResponse.data;
              const formattedData = graph_data.dates.map((date, index) => ({
                date,
                price: graph_data.prices[index],
                smaShort: graph_data.sma_short[index],
                smaLong: graph_data.sma_long[index]
              }));
              if (!cancelled) setSMAGraphData({ data: formattedData, currency });
            } else {
              if (!cancelled) setSMAGraphData({ data: [], currency: 'USD' });
            }
          } catch (error) {
            console.error("Error fetching SMA graph: ", error);
            if (!cancelled) setSMAGraphData({ data: [], currency: 'USD' });
          }
        } else {
          message.error('Failed to fetch data for the ticker.');
          if (!cancelled) setHasValidData(false);
        }
      } catch (error) {
        console.error("Error fetching stock data: ", error);
        if (!cancelled) setHasValidData(false);
      } finally {
        if (!cancelled) stopLoading();
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [tickerValue]);

  // DAS-2250: No startLoading/stopLoading — favorites check is fast and shouldn't block the modal
  useEffect(() => {
    if (!userId || !tickerValue) return;
    let cancelled = false;
    const checkFavorite = async () => {
      try {
        const response = await api.post(`/check_favorites/${userId}`, { ticker: tickerValue });
        if (!cancelled) setIsFavorite(response.data.isFavorite);
      } catch (e) {
        console.error('Error checking favorites:', e);
      }
    };
    checkFavorite();
    return () => { cancelled = true; };
  }, [userId, tickerValue]);

  const handleCancel = () => onClose();

  const handleValueClick = async (record) => {
    try {
      const response = await api.get('/external_link', {
        params: { ticker: tickerValue, field_name: record.originalField },
      });
      if (response.data && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        console.warn('No valid URL returned from the API.');
      }
    } catch (error) {
      console.error('Error fetching URL:', error);
    }
  };

  const handleFavoriteChange = () => {
    setIsFavorite(!isFavorite);
    setFavoriteChanged(true);
  };

  const handleSaveFavorite = async () => {
    if (!favoriteChanged) return;
    const response = await api.post(`/change_favorites/${userId}`, [{ ticker: tickerValue, isFavorite: isFavorite }]);
    message.success(response.data.message, 3);
    setFavoriteChanged(false);
  };

  const [DD_dataset_json, setDD_dataset_json] = useState([]);

  useEffect(() => {
    if (!tickerValue) return;
    // DAS-2250: No startLoading/stopLoading — data delta shouldn't block the modal
    let cancelled = false;
    const fetchDataDeltaForTicker = async () => {
      try {
        const response = await api.post('/Data_Delta_load_for_ticker', { ticker: tickerValue });
        if (response.status === 200) {
          if (!cancelled) setDD_dataset_json(response.data.DD_dataset_json);
        } else {
          message.error('Failed to fetch Data Delta for the ticker.');
        }
      } catch (error) {
        console.error('Error fetching DD_dataset_json:', error);
        message.error('An error occurred while fetching Data Delta.');
      }
    };
    fetchDataDeltaForTicker();
    return () => { cancelled = true; };
  }, [tickerValue]);

  const getTooltip = (dataField) => {
    const match = DP_dataset_json?.find(entry => entry.DataField === dataField);
    return match?.Tooltip || 'No tooltip available';
  };

  const getFundamentalTrendTooltip = () => {
    // Look for the correct DataField in DP_dataset_json for fundamental trend analysis
    const fundamentalTrendEntry = DP_dataset_json?.find(entry => 
      entry.DataField === 'fundamental_trend_analysis_text'
    );
    return fundamentalTrendEntry?.Tooltip || 'Fundamental trend analysis based on EPS, Revenue, Margins, ROE, and FCF trends';
  };

  // DAS-1887: Download PDF handler
  const handleDownloadPDF = async () => {
    if (!contentRef.current || !hasValidData || !tickerValue) {
      message.error('Content not ready for PDF generation');
      return;
    }
    
    message.loading('Generating PDF...', 0);
    
    try {
      const element = contentRef.current;
      
      // Find the DialogContent and temporarily adjust styles for PDF capture
      const dialogContent = element.closest('.MuiDialogContent-root');
      const originalOverflow = dialogContent?.style.overflow;
      const originalHeight = dialogContent?.style.height;
      const originalMaxHeight = dialogContent?.style.maxHeight;
      
      if (dialogContent) {
        dialogContent.style.overflow = 'visible';
        dialogContent.style.height = 'auto';
        dialogContent.style.maxHeight = 'none';
      }
      
      element.style.overflow = 'visible';
      element.style.height = 'auto';
      
      // Wait for reflow
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const fullWidth = element.scrollWidth || element.offsetWidth;
      const fullHeight = element.scrollHeight || element.offsetHeight;
      
      console.log('PDF capture dimensions:', fullWidth, 'x', fullHeight);
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scrollX: 0,
        scrollY: 0,
      });
      
      // Restore styles
      if (dialogContent) {
        dialogContent.style.overflow = originalOverflow || '';
        dialogContent.style.height = originalHeight || '';
        dialogContent.style.maxHeight = originalMaxHeight || '';
      }
      element.style.overflow = '';
      element.style.height = '';
      
      console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const titleHeight = 30;
      const usableHeight = pdfHeight - 2 * margin - titleHeight;
      const usableWidth = pdfWidth - 2 * margin;
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const widthRatio = usableWidth / imgWidth;
      const scaledTotalHeight = imgHeight * widthRatio;
      const totalPages = Math.ceil(scaledTotalHeight / usableHeight);
      const sourceHeightPerPage = usableHeight / widthRatio;
      
      console.log('PDF will have', totalPages, 'pages');
      
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Ticker_${tickerValue}_${currentDate}.pdf`;
      
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        pdf.setFontSize(14);
        pdf.setTextColor(51, 51, 51);
        pdf.text(`${stockValue || tickerValue} (${tickerValue}) - Page ${page + 1}/${totalPages}`, margin, margin + 12);
        
        const sourceY = page * sourceHeightPerPage;
        const sourceHeight = Math.min(sourceHeightPerPage, imgHeight - sourceY);
        
        if (sourceHeight <= 0) break;
        
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgWidth;
        pageCanvas.height = Math.ceil(sourceHeight);
        const ctx = pageCanvas.getContext('2d');
        
        ctx.drawImage(
          canvas,
          0, sourceY,
          imgWidth, sourceHeight,
          0, 0,
          imgWidth, sourceHeight
        );
        
        const pageImgData = pageCanvas.toDataURL('image/png');
        const destHeight = sourceHeight * widthRatio;
        
        pdf.addImage(pageImgData, 'PNG', margin, margin + titleHeight, usableWidth, destHeight);
      }
      
      pdf.save(filename);
      message.destroy();
      message.success(`PDF downloaded: ${filename} (${totalPages} pages)`);
    } catch (err) {
      message.destroy();
      message.error('Failed to generate PDF');
      console.error('PDF generation error:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '1200px', // Adjust maxWidth for larger screens
          margin: '20px auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '1200px', // Adjust maxWidth for larger screens
          margin: '20px auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        data-testid="search-result-modal-title" // DAS-3249: proves the modal opened
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '10px',
          borderBottom: '1px solid #eee',
        }}
      >
        <div>
          <Checkbox checked={isFavorite} onChange={handleFavoriteChange} />
          {`Data for ${stockValue} (ticker '${tickerValue}')`}
          {isTickerInPortfolio && <span> - Part of your Portfolio!</span>}
        </div>
        <div>
          <Button onClick={handleCancel} disabled={isLoading} style={{ marginRight: '8px' }}>
            OK
          </Button>
          <Button type="default" onClick={() => setLlmModalOpen(true)} disabled={!hasValidData || isLoading} style={{ marginRight: '8px' }}>
            🔍 LLM View
          </Button>
          <Button type="default" onClick={handleDownloadPDF} disabled={!hasValidData || isLoading} style={{ marginRight: '8px' }}>
            📄 Download PDF
          </Button>
          <Button className="save-button" type="primary" disabled={!favoriteChanged || isLoading} onClick={handleSaveFavorite}>
            Save
          </Button>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <div ref={contentRef}>
          {isLoading && !hasValidData ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" tip="Loading data…" />
            </div>
          ) : hasValidData ? (
              <>
              {smaGraphData ? (
                <SMAGraph data={smaGraphData.data} currency={smaGraphData.currency} transactions={transactionData} />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                  <Spin size="small" /> <span style={{ marginLeft: 8 }}>Loading chart…</span>
                </div>
              )}

              {isTickerInPortfolio && tickerPortfolioData && (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <h2 style={{ margin: 0 }}>Position Details</h2>
                  <PositionDetails tickerPortfolioData={tickerPortfolioData} maybeBlur={maybeBlur} />
                </div>
              )}

              {stockData && (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <h2 style={{ margin: 0 }}>Ticker Fundamentals</h2>
                  {stockData.fundamentals_trend_analysis_data?.fundamental_trend_label && (
                    <>
                      <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                        <span
                          onClick={(e) => setTrendAnchorEl(e.currentTarget)}
                          style={{
                            color: /Improvement/i.test(stockData.fundamentals_trend_analysis_data.fundamental_trend_label)
                              ? 'green'
                              : /Deterioration/i.test(stockData.fundamentals_trend_analysis_data.fundamental_trend_label)
                              ? 'red'
                              : '#888',
                            cursor: 'pointer',
                            textDecoration: 'underline dotted',
                          }}
                        >
                          Trend: {stockData.fundamentals_trend_analysis_data.fundamental_trend_label}
                        </span>
                      </div>

                      <Popover
                        open={trendPopoverOpen}
                        anchorEl={trendAnchorEl}
                        onClose={() => setTrendAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        PaperProps={{ style: { padding: '15px', maxWidth: '500px' } }}
                      >
                        <Tooltip title={getFundamentalTrendTooltip()} arrow placement="top">
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Fundamental Trend Analysis
                          </Typography>
                        </Tooltip>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', marginBottom: '10px' }}>
                          {stockData.fundamentals_trend_analysis_data.fundamental_trend_analysis_text}
                        </Typography>
                        
                        {/* DAS-1903: New fundamental trend fields
                            DAS-2395: Relabelled for disambiguation. The Label is
                            a backward-looking TREND signal; the Composite adds
                            forward outlook (attenuated by credibility). They can
                            legitimately diverge — see docs/DR_fundamental_trend_analysis.md
                            section "Label vs. final_score — why they can DIVERGE". */}
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                            Score Details:
                          </Typography>
                          {stockData.fundamentals_trend_analysis_data.fundamental_trend_score !== null && stockData.fundamentals_trend_analysis_data.fundamental_trend_score !== undefined && (
                            <Tooltip title="Raw weighted sum across the 5 fundamental pillars (EPS, Revenue, Margins, ROE, FCF). Internal intermediate — drives the Label. Range roughly [-25, +29]." arrow placement="left">
                              <Typography variant="caption" display="block">
                                Trend Score (raw): {stockData.fundamentals_trend_analysis_data.fundamental_trend_score}
                              </Typography>
                            </Tooltip>
                          )}
                          {(stockData.fundamentals_trend_analysis_data.fundamental_composite_score ?? stockData.fundamentals_trend_analysis_data.fundamental_final_score) !== null && (stockData.fundamentals_trend_analysis_data.fundamental_composite_score ?? stockData.fundamentals_trend_analysis_data.fundamental_final_score) !== undefined && (
                            <Tooltip title="Composite signal: backward trend + forward outlook (analyst growth estimates), attenuated by credibility when forward data exists. CAN DIVERGE from the Label, which is backward-only. Percentage scale, roughly [-86, +120]." arrow placement="left">
                              <Typography variant="caption" display="block">
                                Composite (trend + forward): {stockData.fundamentals_trend_analysis_data.fundamental_composite_score ?? stockData.fundamentals_trend_analysis_data.fundamental_final_score}%
                              </Typography>
                            </Tooltip>
                          )}
                          {stockData.fundamentals_trend_analysis_data.fundamental_forward_score !== null && stockData.fundamentals_trend_analysis_data.fundamental_forward_score !== undefined && (
                            <Tooltip title="Forward-only signal from analyst growth estimates (eps_growth_fy + revenue_growth_fy). Percentage scale. Used as the 'forward' input to the Composite." arrow placement="left">
                              <Typography variant="caption" display="block">
                                Forward Score: {stockData.fundamentals_trend_analysis_data.fundamental_forward_score}%
                              </Typography>
                            </Tooltip>
                          )}
                          {stockData.fundamentals_trend_analysis_data.fundamental_credibility !== null && stockData.fundamentals_trend_analysis_data.fundamental_credibility !== undefined && (
                            <Tooltip title="Confidence weight on the forward signal. Currently used only inside the Composite (does not yet affect the Label — see DAS-2399)." arrow placement="left">
                              <Typography variant="caption" display="block">
                                Credibility: {(parseFloat(stockData.fundamentals_trend_analysis_data.fundamental_credibility) * 100).toFixed(1)}%
                              </Typography>
                            </Tooltip>
                          )}
                          {stockData.fundamentals_trend_analysis_data.fundamental_trend_validity_days !== null && stockData.fundamentals_trend_analysis_data.fundamental_trend_validity_days !== undefined && (
                            <Typography variant="caption" display="block">
                              Validity: {stockData.fundamentals_trend_analysis_data.fundamental_trend_validity_days} days
                            </Typography>
                          )}
                        </div>

                        {/* DAS-1884: Change tracking fields */}
                        {(stockData.fundamentals_trend_analysis_data.fundamental_trend_label_previous || 
                          stockData.fundamentals_trend_analysis_data.fundamental_trend_label_changed_at ||
                          stockData.fundamentals_trend_analysis_data.fundamental_trend_label_direction) && (
                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                              Change History:
                            </Typography>
                            {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_previous && (
                              <Typography variant="caption" display="block">
                                Previous Label: {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_previous}
                              </Typography>
                            )}
                            {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_changed_at && (
                              <Typography variant="caption" display="block">
                                Changed At: {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_changed_at}
                              </Typography>
                            )}
                            {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_direction && (
                              <Typography variant="caption" display="block" style={{
                                color: stockData.fundamentals_trend_analysis_data.fundamental_trend_label_direction === 'positive' ? 'green' :
                                       stockData.fundamentals_trend_analysis_data.fundamental_trend_label_direction === 'negative' ? 'red' : '#888'
                              }}>
                                Direction: {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_direction}
                              </Typography>
                            )}
                            {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_change_magnitude !== null && 
                             stockData.fundamentals_trend_analysis_data.fundamental_trend_label_change_magnitude !== undefined && (
                              <Typography variant="caption" display="block">
                                Change Magnitude: {stockData.fundamentals_trend_analysis_data.fundamental_trend_label_change_magnitude} steps
                              </Typography>
                            )}
                          </div>
                        )}

                        {/* DAS-1951: Trend Weighting Section with Magnitude */}
                        {stockData.fundamentals_trend_analysis_data.trend_weighting && (
                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                              Trend Weighting:
                            </Typography>
                            <Typography variant="caption" display="block" style={{ marginBottom: '6px' }}>
                              {stockData.fundamentals_trend_analysis_data.trend_weighting.explanation}
                            </Typography>
                            
                            {/* Field details with weight, % change, and magnitude */}
                            <div style={{ 
                              backgroundColor: '#f5f5f5',
                              padding: '8px',
                              borderRadius: '4px',
                              fontSize: '11px'
                            }}>
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '55px 45px 55px 50px 55px', 
                                gap: '2px 6px',
                                fontWeight: 'bold',
                                borderBottom: '1px solid #ddd',
                                paddingBottom: '4px',
                                marginBottom: '4px',
                                fontSize: '11px'
                              }}>
                                <span>Field</span>
                                <span>Weight</span>
                                <span>Change</span>
                                <span>vs Sect</span>
                                <span>Impact</span>
                              </div>
                              {stockData.fundamentals_trend_analysis_data.trend_weighting.field_details && 
                                Object.entries(stockData.fundamentals_trend_analysis_data.trend_weighting.field_details)
                                  .sort((a, b) => (b[1].weight_pct || 0) - (a[1].weight_pct || 0))
                                  .map(([field, details]) => (
                                    <div key={field} style={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: '55px 45px 55px 50px 55px', 
                                      gap: '2px 6px',
                                      padding: '2px 0',
                                      fontSize: '11px'
                                    }}>
                                      <span style={{ fontWeight: '500' }}>{field}</span>
                                      <span style={{ 
                                        color: details.weight_pct > 22 ? '#1976d2' : details.weight_pct < 15 ? '#999' : '#333'
                                      }}>
                                        {details.weight_pct}%
                                      </span>
                                      <span style={{ 
                                        color: details.pct_change > 0 ? 'green' : details.pct_change < 0 ? 'red' : '#666'
                                      }}>
                                        {details.pct_change !== null ? `${details.pct_change > 0 ? '+' : ''}${details.pct_change}%` : 'n/a'}
                                      </span>
                                      <span style={{ 
                                        color: details.vs_sector > 2 ? '#2e7d32' : details.vs_sector < -2 ? '#c62828' : '#666'
                                      }}>
                                        {details.vs_sector !== null ? `${details.vs_sector > 0 ? '+' : ''}${details.vs_sector}%` : 'n/a'}
                                      </span>
                                      <span style={{ 
                                        color: details.sector_adj > 1.0 ? '#2e7d32' : details.sector_adj < 1.0 ? '#c62828' : '#666'
                                      }}>
                                        ×{details.magnitude}×{details.sector_adj}
                                      </span>
                                    </div>
                                  ))
                              }
                              {/* Fallback for old format (field_weights instead of field_details) */}
                              {!stockData.fundamentals_trend_analysis_data.trend_weighting.field_details && 
                               stockData.fundamentals_trend_analysis_data.trend_weighting.field_weights &&
                                Object.entries(stockData.fundamentals_trend_analysis_data.trend_weighting.field_weights)
                                  .sort((a, b) => b[1] - a[1])
                                  .map(([field, weight]) => (
                                    <div key={field} style={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: '55px 45px 55px 50px 55px', 
                                      gap: '2px 6px',
                                      padding: '2px 0',
                                      fontSize: '11px'
                                    }}>
                                      <span style={{ fontWeight: '500' }}>{field}</span>
                                      <span>{weight}%</span>
                                      <span style={{ color: '#999' }}>n/a</span>
                                      <span style={{ color: '#999' }}>n/a</span>
                                      <span style={{ color: '#999' }}>n/a</span>
                                    </div>
                                  ))
                              }
                            </div>
                            
                            <Typography variant="caption" color="text.secondary" display="block" style={{ marginTop: '6px', fontStyle: 'italic' }}>
                              Impact = Magnitude × Sector Adj (×1.2 = outperforming, ×0.8 = lagging)</Typography>
                          </div>
                        )}
                        
                        <hr style={{ marginTop: '10px' }} />
                        <Typography variant="caption" color="text.secondary">
                          Last Updated: {stockData.fundamentals_trend_analysis_data.fundamental_trend_updated_at}
                        </Typography>
                      </Popover>
                    </>
                  )}

                  {/* DAS-1928: Relative Fundamental Position indicator */}
                  {/* DAS-1929: Enhanced to show weight source, regime, and field-level specificity */}
                  {stockData.fundamental_position_data?.composite_score !== undefined && (
                    <div style={{ fontSize: '13px', marginBottom: '10px', marginTop: '5px' }}>
                      <Tooltip
                        title={
                          <div style={{ maxWidth: '480px', padding: '8px' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                              {stockData.fundamental_position_data.context?.explanation}
                            </div>
                            
                            {/* DAS-1929/DAS-1931: Weight source with regime info and correlation breakdown */}
                            <div style={{ 
                              marginBottom: '8px', 
                              padding: '6px 8px', 
                              backgroundColor: stockData.fundamental_position_data.weight_source?.high_confidence_count > 0 
                                ? '#1a3d1a' 
                                : stockData.fundamental_position_data.weight_source?.is_sector_specific 
                                  ? '#2d3d1a' 
                                  : '#3d3d1a',
                              borderRadius: '4px',
                              fontSize: '10px'
                            }}>
                              <div>
                                <span style={{ fontWeight: 'bold' }}>Market regime: </span>
                                <span style={{ color: '#fff' }}>{stockData.fundamental_position_data.weight_source?.regime || 'N/A'}</span>
                                {stockData.fundamental_position_data.weight_source?.is_sector_specific && (
                                  <span style={{ marginLeft: '10px' }}>
                                    | <span style={{ color: '#90EE90' }}>★ {stockData.fundamental_position_data.weight_source?.high_confidence_count || 0}</span>
                                    {' '}
                                    <span style={{ color: '#EEEE90' }}>◆ {(stockData.fundamental_position_data.weight_source?.sector_specific_count || 0) - (stockData.fundamental_position_data.weight_source?.high_confidence_count || 0)}</span>
                                    {' '}fields using correlation data
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div style={{ marginBottom: '8px' }}>
                              <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '4px' }}>
                                Top 7 of {stockData.fundamental_position_data.total_fields || '?'} fields (remaining {(stockData.fundamental_position_data.total_fields || 21) - 7} have weights &lt;3%):
                              </div>
                              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid #555' }}>
                                    <th style={{ textAlign: 'left', padding: '4px' }}>Field</th>
                                    <th style={{ textAlign: 'right', padding: '4px' }}>Pctl</th>
                                    <th style={{ textAlign: 'right', padding: '4px' }}>Weight</th>
                                    <th style={{ textAlign: 'right', padding: '4px' }}>Contrib</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {stockData.fundamental_position_data.breakdown?.slice(0, 7).map((item, idx) => (
                                    <tr key={idx} style={{ 
                                      borderBottom: '1px solid #333',
                                      // DAS-1931: High confidence (≥5%) gets green, low confidence (2-5%) gets yellow-ish
                                      backgroundColor: item.is_high_confidence 
                                        ? 'rgba(144, 238, 144, 0.15)' 
                                        : item.is_sector_specific 
                                          ? 'rgba(238, 238, 144, 0.10)' 
                                          : 'transparent'
                                    }}>
                                      <td style={{ padding: '3px 4px' }}>
                                        {item.field}
                                        {/* DAS-1931: Star only for high confidence (≥5% correlation) */}
                                        {item.is_high_confidence && <span style={{ color: '#90EE90', marginLeft: '4px' }} title="Strong correlation ≥5%">★</span>}
                                        {/* Show different indicator for weak correlation (2-5%) */}
                                        {item.is_sector_specific && !item.is_high_confidence && <span style={{ color: '#EEEE90', marginLeft: '4px' }} title="Weak correlation 2-5%">◆</span>}
                                      </td>
                                      <td style={{ textAlign: 'right', padding: '3px 4px' }}>{item.sector_percentile}th</td>
                                      <td style={{ textAlign: 'right', padding: '3px 4px' }}>{item.weight}%</td>
                                      <td style={{ textAlign: 'right', padding: '3px 4px' }}>{item.contribution.toFixed(3)}</td>
                                    </tr>
                                  ))}
                                  <tr style={{ fontWeight: 'bold', borderTop: '2px solid #555' }}>
                                    <td colSpan={3} style={{ padding: '4px' }}>COMPOSITE</td>
                                    <td style={{ textAlign: 'right', padding: '4px' }}>
                                      {stockData.fundamental_position_data.composite_score.toFixed(2)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                              <div style={{ fontSize: '9px', color: '#666', marginTop: '6px', lineHeight: '1.4' }}>
                                {/* DAS-1931: Updated legend for three tiers */}
                                <div><span style={{ color: '#90EE90' }}>★</span> = Strong correlation ≥5% → high confidence weight</div>
                                <div style={{ marginTop: '2px' }}><span style={{ color: '#EEEE90' }}>◆</span> = Weak correlation 2-5% → using actual data (lower confidence)</div>
                                <div style={{ marginTop: '2px' }}>No symbol = Correlation &lt;2% → hardcoded default weight</div>
                              </div>
                            </div>
                            
                            <div style={{ fontSize: '10px', color: '#aaa', fontStyle: 'italic', marginTop: '6px', padding: '4px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '3px' }}>
                              {stockData.fundamental_position_data.context?.note}
                            </div>
                            
                            {/* DAS-1934: Historical Performance Context */}
                            {stockData.fundamental_position_data.historical_context?.show_context && (
                              <div style={{ 
                                marginTop: '10px', 
                                padding: '8px', 
                                backgroundColor: 'rgba(100, 149, 237, 0.15)', 
                                borderRadius: '4px',
                                borderLeft: `3px solid ${
                                  stockData.fundamental_position_data.historical_context.reliability === 'HIGH' ? '#90EE90' :
                                  stockData.fundamental_position_data.historical_context.reliability === 'MEDIUM' ? '#FFD700' : '#888'
                                }`
                              }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '6px', color: '#87CEEB' }}>
                                  📊 What the model implies at this rank ({stockData.fundamental_position_data.historical_context.regime} market)
                                </div>

                                <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
                                  {/* DAS-3303: no number (too little correlation data) -> the backend's own 'insufficient data' sentence */}
                                  {stockData.fundamental_position_data.historical_context.hit_rate != null
                                    ? <>The model implies a {stockData.fundamental_position_data.historical_context.hit_rate.toFixed(0)}% chance of beating the sector median for stocks at this rank.</>
                                    : stockData.fundamental_position_data.historical_context.summary}
                                </div>
                                
                                {stockData.fundamental_position_data.historical_context.show_numbers && (
                                  <div style={{ marginTop: '8px', fontSize: '10px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                      <tbody>
                                        <tr>
                                          <td style={{ padding: '2px 0', color: '#aaa' }}>Beat sector median:</td>
                                          <td style={{ 
                                            padding: '2px 8px', 
                                            fontWeight: 'bold',
                                            color: stockData.fundamental_position_data.historical_context.hit_rate >= 55 ? '#90EE90' : 
                                                   stockData.fundamental_position_data.historical_context.hit_rate <= 45 ? '#FF6B6B' : '#FFD700'
                                          }}>
                                            {stockData.fundamental_position_data.historical_context.hit_rate?.toFixed(0)}% of the time
                                          </td>
                                        </tr>
                                        <tr>
                                          <td style={{ padding: '2px 0', color: '#aaa' }}>Typical excess return:</td>
                                          <td style={{ 
                                            padding: '2px 8px', 
                                            fontWeight: 'bold',
                                            color: stockData.fundamental_position_data.historical_context.median_alpha > 0 ? '#90EE90' : 
                                                   stockData.fundamental_position_data.historical_context.median_alpha < 0 ? '#FF6B6B' : '#FFD700'
                                          }}>
                                            {stockData.fundamental_position_data.historical_context.median_alpha > 0 ? '+' : ''}
                                            {stockData.fundamental_position_data.historical_context.median_alpha?.toFixed(1)}% per year vs sector
                                          </td>
                                        </tr>
                                        <tr>
                                          <td style={{ padding: '2px 0', color: '#aaa' }}>Typical range (IQR):</td>
                                          <td style={{ padding: '2px 8px', color: '#ccc' }}>
                                            {stockData.fundamental_position_data.historical_context.range_low?.toFixed(0)}% to {stockData.fundamental_position_data.historical_context.range_high > 0 ? '+' : ''}{stockData.fundamental_position_data.historical_context.range_high?.toFixed(0)}% vs sector
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  marginTop: '8px',
                                  fontSize: '9px',
                                  color: '#888'
                                }}>
                                  <span>
                                    Reliability: 
                                    <span style={{ 
                                      marginLeft: '4px',
                                      fontWeight: 'bold',
                                      color: stockData.fundamental_position_data.historical_context.reliability === 'HIGH' ? '#90EE90' :
                                             stockData.fundamental_position_data.historical_context.reliability === 'MEDIUM' ? '#FFD700' : '#888'
                                    }}>
                                      {stockData.fundamental_position_data.historical_context.reliability}
                                    </span>
                                  </span>
                                </div>
                                
                                <div style={{ fontSize: '8px', color: '#666', marginTop: '6px', fontStyle: 'italic', lineHeight: '1.4' }}>
                                  "Sector median" = 50th percentile stock in {stockData.fundamental_position_data.sector}. 
                                  Model implication, not a measured result. Wide range reflects weak correlation signal.
                                </div>
                              </div>
                            )}
                            
                            {/* DAS-1955: Per-field Alpha Contributions */}
                            {stockData.fundamental_position_data.alpha_contributions && 
                             stockData.fundamental_position_data.alpha_contributions.fields_used > 0 && (
                              <div style={{ 
                                marginTop: '12px',
                                padding: '10px',
                                backgroundColor: '#1a2a1a',
                                borderRadius: '6px',
                                borderLeft: `3px solid ${
                                  stockData.fundamental_position_data.alpha_contributions.total_expected_alpha > 1 ? '#90EE90' :
                                  stockData.fundamental_position_data.alpha_contributions.total_expected_alpha < -1 ? '#FF6B6B' : '#FFD700'
                                }`
                              }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', color: '#87CEEB' }}>
                                  📈 Model expectation ({stockData.fundamental_position_data.alpha_contributions.regime} market)
                                </div>

                                <div data-testid="fundamental-model-note" style={{ fontSize: '9px', color: '#999', fontStyle: 'italic', marginBottom: '8px', lineHeight: '1.4' }}>
                                  A calculation from the correlations above, not a measured result. Tested on past dates: no stock-picking power (Account -&gt; Alpha).
                                </div>

                                {/* Summary */}
                                <div style={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '1fr 1fr', 
                                  gap: '4px 12px',
                                  marginBottom: '10px',
                                  fontSize: '11px'
                                }}>
                                  <div>
                                    <span style={{ color: '#aaa' }}>Model expectation:</span>
                                    <span style={{ 
                                      marginLeft: '6px',
                                      fontWeight: 'bold',
                                      color: stockData.fundamental_position_data.alpha_contributions.total_expected_alpha > 0 ? '#90EE90' : 
                                             stockData.fundamental_position_data.alpha_contributions.total_expected_alpha < 0 ? '#FF6B6B' : '#FFD700'
                                    }}>
                                      {stockData.fundamental_position_data.alpha_contributions.total_expected_alpha > 0 ? '+' : ''}
                                      {stockData.fundamental_position_data.alpha_contributions.total_expected_alpha}% /yr
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#aaa' }}>P(beats sector, model):</span>
                                    <span style={{ 
                                      marginLeft: '6px',
                                      fontWeight: 'bold',
                                      color: stockData.fundamental_position_data.alpha_contributions.probability_positive_alpha >= 55 ? '#90EE90' : 
                                             stockData.fundamental_position_data.alpha_contributions.probability_positive_alpha <= 45 ? '#FF6B6B' : '#FFD700'
                                    }}>
                                      {stockData.fundamental_position_data.alpha_contributions.probability_positive_alpha}%
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Per-field breakdown */}
                                <div style={{ fontSize: '10px' }}>
                                  <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: '70px 35px 35px 50px', 
                                    gap: '2px 6px',
                                    fontWeight: 'bold',
                                    borderBottom: '1px solid #444',
                                    paddingBottom: '3px',
                                    marginBottom: '3px',
                                    color: '#aaa'
                                  }}>
                                    <span>Field</span>
                                    <span>Pctl</span>
                                    <span>r</span>
                                    <span>→ Model</span>
                                  </div>
                                  {Object.entries(stockData.fundamental_position_data.alpha_contributions.field_contributions || {})
                                    .sort((a, b) => Math.abs(b[1].contribution_pct) - Math.abs(a[1].contribution_pct))
                                    .slice(0, 5)
                                    .map(([field, data]) => (
                                      <div key={field} style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '70px 35px 35px 50px', 
                                        gap: '2px 6px',
                                        padding: '2px 0'
                                      }}>
                                        <span style={{ color: '#ccc' }}>{field}</span>
                                        <span style={{ color: data.percentile >= 60 ? '#90EE90' : data.percentile <= 40 ? '#FF6B6B' : '#ccc' }}>
                                          {data.percentile}th
                                        </span>
                                        <span style={{ color: '#888' }}>{data.correlation?.toFixed(2)}</span>
                                        <span style={{ 
                                          color: data.contribution_pct > 0.5 ? '#90EE90' : 
                                                 data.contribution_pct < -0.5 ? '#FF6B6B' : '#888',
                                          fontWeight: Math.abs(data.contribution_pct) > 1 ? 'bold' : 'normal'
                                        }}>
                                          {data.contribution_pct > 0 ? '+' : ''}{data.contribution_pct}%
                                        </span>
                                      </div>
                                    ))
                                  }
                                </div>
                                
                                <div style={{ 
                                  marginTop: '8px',
                                  fontSize: '9px',
                                  color: '#888',
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}>
                                  <span>
                                    Reliability: 
                                    <span style={{ 
                                      marginLeft: '4px',
                                      fontWeight: 'bold',
                                      color: stockData.fundamental_position_data.alpha_contributions.reliability === 'HIGH' ? '#90EE90' :
                                             stockData.fundamental_position_data.alpha_contributions.reliability === 'MEDIUM' ? '#FFD700' : '#888'
                                    }}>
                                      {stockData.fundamental_position_data.alpha_contributions.reliability}
                                    </span>
                                  </span>
                                  <span>{stockData.fundamental_position_data.alpha_contributions.fields_used} fields used</span>
                                </div>
                                
                                <div style={{ fontSize: '8px', color: '#666', marginTop: '6px', fontStyle: 'italic' }}>
                                  Model expectation = correlation × rank score × assumed volatility, summed over fields; not measured.
                                </div>
                              </div>
                            )}
                            
                            {/* DAS-1956: Return Probability Section */}
                            {stockData.fundamental_position_data.return_probability && (
                              <div style={{ 
                                marginTop: '12px',
                                padding: '10px',
                                backgroundColor: 'rgba(100,149,237,0.15)',
                                borderRadius: '6px',
                                borderLeft: `3px solid ${
                                  stockData.fundamental_position_data.return_probability.probability_positive_return >= 60 ? '#90EE90' :
                                  stockData.fundamental_position_data.return_probability.probability_positive_return <= 40 ? '#FF6B6B' : '#FFD700'
                                }`
                              }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', color: '#6495ED' }}>
                                  📊 Return Probability ({stockData.fundamental_position_data.return_probability.regime} market)
                                </div>

                                <div style={{ fontSize: '9px', color: '#999', fontStyle: 'italic', marginBottom: '8px', lineHeight: '1.4' }}>
                                  A calculation from the correlations above, not a measured result. Tested on past dates: no stock-picking power (Account -&gt; Alpha).
                                </div>

                                {/* Main probability display */}
                                <div style={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '1fr 1fr', 
                                  gap: '4px 12px',
                                  marginBottom: '10px',
                                  fontSize: '11px'
                                }}>
                                  <div>
                                    <span style={{ color: '#ddd' }}>P(positive return):</span>
                                    <span style={{ 
                                      marginLeft: '6px',
                                      fontWeight: 'bold',
                                      fontSize: '13px',
                                      color: stockData.fundamental_position_data.return_probability.probability_positive_return >= 60 ? '#90EE90' : 
                                             stockData.fundamental_position_data.return_probability.probability_positive_return <= 40 ? '#FF6B6B' : '#FFD700'
                                    }}>
                                      {stockData.fundamental_position_data.return_probability.probability_positive_return}%
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#ddd' }}>Expected Q return:</span>
                                    <span style={{ 
                                      marginLeft: '6px',
                                      fontWeight: 'bold',
                                      color: stockData.fundamental_position_data.return_probability.expected_quarterly_return > 0 ? '#90EE90' : 
                                             stockData.fundamental_position_data.return_probability.expected_quarterly_return < 0 ? '#FF6B6B' : '#FFD700'
                                    }}>
                                      {stockData.fundamental_position_data.return_probability.expected_quarterly_return > 0 ? '+' : ''}
                                      {stockData.fundamental_position_data.return_probability.expected_quarterly_return}%
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Breakdown */}
                                <div style={{ 
                                  fontSize: '10px',
                                  backgroundColor: 'rgba(0,0,0,0.3)',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{ marginBottom: '4px', color: '#bbb', fontWeight: 'bold', fontSize: '9px' }}>CALCULATION:</div>
                                  <div style={{ marginBottom: '3px' }}>
                                    <span style={{ color: '#ddd' }}>① Historical {stockData.fundamental_position_data.sector} avg in {stockData.fundamental_position_data.return_probability.regime}:</span>
                                    <span style={{ marginLeft: '6px', color: '#fff', fontWeight: 'bold' }}>
                                      {stockData.fundamental_position_data.return_probability.base_sector_return_quarterly > 0 ? '+' : ''}
                                      {stockData.fundamental_position_data.return_probability.base_sector_return_quarterly}%
                                    </span>
                                  </div>
                                  <div style={{ marginBottom: '3px' }}>
                                    <span style={{ color: '#ddd' }}>② Boost from this stock's fundamentals:</span>
                                    <span style={{ 
                                      marginLeft: '6px', 
                                      fontWeight: 'bold',
                                      color: stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly > 0 ? '#90EE90' : 
                                             stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly < 0 ? '#FF6B6B' : '#fff'
                                    }}>
                                      {stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly > 0 ? '+' : ''}
                                      {stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly}%
                                    </span>
                                  </div>
                                  <div style={{ marginTop: '6px', paddingTop: '4px', borderTop: '1px solid #555' }}>
                                    <span style={{ color: '#ddd' }}>③ Quarterly volatility:</span>
                                    <span style={{ marginLeft: '6px', color: '#FF9800', fontWeight: 'bold' }}>
                                      ±{stockData.fundamental_position_data.return_probability.quarterly_volatility}%
                                    </span>
                                    <span style={{ color: '#aaa', fontSize: '9px', marginLeft: '6px' }}>
                                      (actual returns range widely)
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Clear Explanation */}
                                <div style={{ 
                                  fontSize: '10px', 
                                  color: '#fff', 
                                  backgroundColor: 'rgba(0,0,0,0.25)',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  lineHeight: '1.5'
                                }}>
                                  <strong style={{ color: '#6495ED' }}>Plain English:</strong>{' '}
                                  {stockData.fundamental_position_data.sector} stocks in {stockData.fundamental_position_data.return_probability.regime} markets 
                                  historically return{' '}
                                  <span style={{ color: '#fff', fontWeight: 'bold' }}>
                                    {stockData.fundamental_position_data.return_probability.base_sector_return_quarterly > 0 ? '+' : ''}
                                    {stockData.fundamental_position_data.return_probability.base_sector_return_quarterly}%/quarter
                                  </span>.
                                  {stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly !== 0 && (
                                    <span>
                                      {' '}This stock's fundamentals add{' '}
                                      <span style={{ 
                                        fontWeight: 'bold',
                                        color: stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly > 0 ? '#90EE90' : '#FF6B6B'
                                      }}>
                                        {stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly > 0 ? '+' : ''}
                                        {stockData.fundamental_position_data.return_probability.alpha_from_fundamentals_quarterly}%
                                      </span>
                                      {' '}(based on correlation analysis; fields with negative values like negative P/B are excluded).
                                    </span>
                                  )}
                                  {' '}Given the{' '}
                                  <span style={{ color: '#FF9800', fontWeight: 'bold' }}>
                                    ±{stockData.fundamental_position_data.return_probability.quarterly_volatility}% volatility
                                  </span>
                                  , there's a{' '}
                                  <span style={{ 
                                    fontWeight: 'bold',
                                    color: stockData.fundamental_position_data.return_probability.probability_positive_return >= 60 ? '#90EE90' : 
                                           stockData.fundamental_position_data.return_probability.probability_positive_return <= 40 ? '#FF6B6B' : '#FFD700'
                                  }}>
                                    {stockData.fundamental_position_data.return_probability.probability_positive_return}%
                                  </span>
                                  {' '}chance the return is positive.
                                </div>
                                
                                {/* Reliability & Sample Size */}
                                <div style={{ 
                                  marginTop: '8px',
                                  fontSize: '9px',
                                  color: '#ccc',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span>
                                    Reliability: 
                                    <span style={{ 
                                      marginLeft: '4px',
                                      fontWeight: 'bold',
                                      color: stockData.fundamental_position_data.return_probability.reliability === 'HIGH' ? '#90EE90' :
                                             stockData.fundamental_position_data.return_probability.reliability === 'MEDIUM' ? '#FFD700' : '#aaa'
                                    }}>
                                      {stockData.fundamental_position_data.return_probability.reliability}
                                    </span>
                                  </span>
                                  <span style={{ color: '#aaa' }}>
                                    Sample: {stockData.fundamental_position_data.return_probability.base_return_sample_size 
                                      ? stockData.fundamental_position_data.return_probability.base_return_sample_size.toLocaleString() 
                                      : 'N/A'} observations
                                  </span>
                                </div>
                                
                                <div style={{ fontSize: '8px', color: '#aaa', marginTop: '6px', fontStyle: 'italic' }}>
                                  {stockData.fundamental_position_data.return_probability.probability_positive_return > 55 
                                    ? `${stockData.fundamental_position_data.return_probability.probability_positive_return}% is better than coin flip (50%). `
                                    : stockData.fundamental_position_data.return_probability.probability_positive_return < 45
                                    ? `${stockData.fundamental_position_data.return_probability.probability_positive_return}% means more likely negative. `
                                    : `${stockData.fundamental_position_data.return_probability.probability_positive_return}% is essentially a coin flip. `}
                                  Use for comparing stocks, not predicting returns.
                                </div>
                              </div>
                            )}
                          </div>
                        }
                        arrow
                        placement="bottom"
                      >
                        <span
                          style={{
                            color: stockData.fundamental_position_data.percentile_value >= 60 ? 'green' :
                                   stockData.fundamental_position_data.percentile_value <= 40 ? 'red' : '#888',
                            cursor: 'pointer',
                            textDecoration: 'underline dotted',
                          }}
                        >
                          Relative Fundamental Position: {stockData.fundamental_position_data.percentile_label} percentile within sector {stockData.fundamental_position_data.sector}
                          {stockData.fundamental_position_data.weight_source?.is_sector_specific && 
                            <span style={{ marginLeft: '6px', fontSize: '10px', color: '#90EE90' }}>●</span>
                          }
                        </span>
                      </Tooltip>
                    </div>
                  )}

                  {/* DAS-3258: Alpha card — which proven register signals this stock carries today */}
                  <AlphaCardBlock ticker={tickerValue} />

                  {/* DAS-1971: Risk-Adj Q Return + Return Probability + Exp Q Return Summary Line */}
                  {/* DAS-2327: tooltip bodies routed through shared helper (returnMetricsTooltips.jsx).
                      The helper's pick() tolerates this component's rp shape
                      (expected_quarterly_return, probability_positive_return, etc.). */}
                  {stockData.fundamental_position_data?.return_probability && (() => {
                    const rp = stockData.fundamental_position_data.return_probability;
                    const expQ = rp.expected_quarterly_return;
                    const resVol = rp.residual_volatility;
                    const prob = rp.probability_positive_return;
                    const riskAdjQ = (expQ != null && resVol != null && resVol > 0) ? Math.round((expQ / resVol) * 1000) / 1000 : null;
                    const riskAdjColor = riskAdjQ >= 0.5 ? '#389e0d' : riskAdjQ >= 0.3 ? '#52c41a' : riskAdjQ >= 0.1 ? '#fa8c16' : riskAdjQ >= 0 ? '#999' : '#ff4d4f';
                    const probColor = prob >= 55 ? '#90EE90' : prob <= 40 ? '#FF6B6B' : '#FFD700';
                    const expQColor = expQ > 0 ? '#90EE90' : expQ < 0 ? '#FF6B6B' : '#FFD700';

                    // Shape the record so the shared helper's pick() normalises correctly.
                    const sharedRecord = {
                      ...rp,
                      Ticker: tickerValue,
                      sector: sectorValue,
                    };

                    return (
                      <div style={{ fontSize: '12px', marginBottom: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                        {/* Reward ÷ Risk */}
                        <Tooltip arrow placement="top" title={<RewardRiskTooltip record={sharedRecord} />}>
                          <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                            <span style={{ color: '#aaa' }}>Reward÷Risk:</span>
                            <span style={{ marginLeft: '4px', fontWeight: 'bold', color: riskAdjColor }}>
                              {riskAdjQ != null ? riskAdjQ.toFixed(2) : 'N/A'}
                            </span>
                          </span>
                        </Tooltip>

                        <span style={{ color: '#555' }}>|</span>

                        {/* Return Probability */}
                        <Tooltip arrow placement="top" title={<ReturnProbabilityTooltip record={sharedRecord} />}>
                          <span style={{ cursor: 'help' }}>
                            <span style={{ color: '#aaa' }}>Return Prob:</span>
                            <span style={{ marginLeft: '4px', fontWeight: 'bold', color: probColor }}>{prob}%</span>
                          </span>
                        </Tooltip>

                        <span style={{ color: '#555' }}>|</span>

                        {/* Expected Q Return */}
                        <Tooltip arrow placement="top" title={<ExpQReturnTooltip record={sharedRecord} />}>
                          <span style={{ cursor: 'help' }}>
                            <span style={{ color: '#aaa' }}>Exp Q Return:</span>
                            <span style={{ marginLeft: '4px', fontWeight: 'bold', color: expQColor }}>
                              {expQ > 0 ? '+' : ''}{expQ}%
                            </span>
                          </span>
                        </Tooltip>
                      </div>
                    );
                  })()}

                  <TickerFundamentals
                    data={stockData}
                    DD_dataset_json={DD_dataset_json}
                    DP_dataset_json={DP_dataset_json}
                    metadata={metadata}
                    tablesData={tablesData}
                    tickerValue={tickerValue}
                    sectorValue={sectorValue}
                  />
                </div>
              )}

              <h2 style={{ textAlign: 'center' }}>Other Data</h2>

              <Stockinfo_TablesContent
                tablesData={tablesData}
                tickerValue={tickerValue}
                DD_dataset_json={DD_dataset_json}
                DP_dataset_json={DP_dataset_json}
                metadata={metadata}
                handleValueClick={handleValueClick}
              />

              <MarketChangesAndTrends tickerValue={tickerValue} />

              {esgDetails && (
                <div className="esg-details">
                  <h3>ESG Details</h3>
                  <ESG_DetailsContent ticker={tickerValue} details={esgDetails} />
                </div>
              )}

              {stockData && stockData.consensus && (() => {
                const explanation = stockData.consensus.consensus_explanation_object;
                return (
                  <div className="trend-consensus" style={{
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    padding: '20px',
                    marginTop: '30px',
                    marginBottom: '30px',
                    backgroundColor: '#fafafa'
                  }}>
                    <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Trend Consensus</h3>

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '40px' }}>
                      <div style={{ flex: 1, display: 'grid', rowGap: '10px', fontSize: '15px' }}>
                        <div><strong>Trend:</strong> {stockData.consensus.consensus_trend_direction}</div>
                        <div><strong>Confidence:</strong> {stockData.consensus.consensus_confidence}</div>
                        <div><strong>Score:</strong> {stockData.consensus.consensus_score}</div>
                        <div><strong>Strength:</strong> {stockData.consensus.consensus_strength}</div>
                        <div><strong>Drivers:</strong>
                          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                            {(() => {
                              const raw = stockData.consensus?.consensus_key_drivers;
                              if (!raw) return <li>N/A</li>;
                              try {
                                const parsed = JSON.parse(raw);
                                if (Array.isArray(parsed)) {
                                  return parsed.map((driver, index) => (
                                    <li key={index}>{driver}</li>
                                  ));
                                } else {
                                  const drivers = raw.split(',').map(d => d.trim());
                                  return drivers.map((driver, index) => (
                                    <li key={index}>{driver}</li>
                                  ));
                                }
                              } catch {
                                const drivers = raw.split(',').map(d => d.trim());
                                return drivers.map((driver, index) => (
                                  <li key={index}>{driver}</li>
                                ));
                              }
                            })()}
                          </ul>
                        </div>
                      </div>

                      <div style={{ flex: 2, fontSize: '14px' }}>
                        <strong>COMPONENT SCORES:</strong> {(() => {
                          const expl = stockData.consensus.consensus_explanation_object;
                          if (!expl || !expl.components) return "Component details not available";
                          
                          const components = expl.components;
                          
                          return (
                            <ul style={{ margin: '4px 0', paddingLeft: '20px', listStyle: 'none' }}>
                              {components.price && (
                                <li>• Price momentum: {Math.round(components.price.score * 100)}/100 ({Math.round(components.price.weight * 100)}% weight)</li>
                              )}
                              {components.sentiment && (
                                <li>• Sentiment: {Math.round(components.sentiment.score * 100)}/100 ({Math.round(components.sentiment.weight * 100)}% weight)</li>
                              )}
                              {components.fundamentals && (
                                <li>• Fundamentals: {Math.round(components.fundamentals.score * 100)}/100 ({Math.round(components.fundamentals.weight * 100)}% weight)</li>
                              )}
                              {components.institutional && (
                                <li>• Institutional flow: {Math.round(components.institutional.score * 100)}/100 ({Math.round(components.institutional.weight * 100)}% weight)</li>
                              )}
                              {components.trendsearch && components.trendsearch.weight > 0 && (
                                <li>• TrendSearch: {Math.round(components.trendsearch.score * 100)}/100 ({Math.round(components.trendsearch.weight * 100)}% weight)</li>
                              )}
                            </ul>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {tickerAlerts.length > 0 && (
                <div className="portfolio-alert">
                  <h3>Portfolio Alerts for {tickerValue}</h3>
                  {tickerAlerts.map((alert, index) => (
                    <p key={index}>{alert.pf_alert_text}</p>
                  ))}
                </div>
              )}

              {pfDataAlertForTicker && (
                <div className="data-alert">
                  <h3>Portfolio Data Alert for {tickerValue}</h3>
                  <p>{pfDataAlertForTicker[4]}</p>
                </div>
              )}

              {favAlert && (
                <div className="alerts-container">
                  <h3>Favorites Alert for {tickerValue}</h3>
                  <p>{favAlert.pf_alert_text}</p>
                </div>
              )}

              {opportunityAlertsForTicker.length > 0 && (
                <div className="opportunity-alerts">
                  <h3>Opportunity Alerts for {tickerValue}</h3>
                  {opportunityAlertsForTicker.map((alert, index) => (
                    <p key={index}>{alert.pf_alert_text}</p>
                  ))}
                </div>
              )}
            </>
            ) : (
              <div>No valid data available for this ticker.</div>
            )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>OK</Button>
        <Button
          className="save-button"
          type="primary"
          disabled={!favoriteChanged || isLoading}
          onClick={handleSaveFavorite}
        >
          Save
        </Button>
      </DialogActions>

      <LLMFormattedPrompt
        visible={llmModalOpen}
        onClose={() => setLlmModalOpen(false)}
        stockData={stockData}
        tablesData={tablesData}
        tickerAlerts={tickerAlerts}
        pfDataAlertForTicker={pfDataAlertForTicker}
        favAlert={favAlert}
        tickerPortfolioData={tickerPortfolioData}
        oppAlert={opportunityAlertsForTicker}
      />
    </Dialog>
  );
};

export default ThreeTablesModal;

